import { useMutation, useReactiveVar } from '@apollo/client';
import { RouteProp, useRoute } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import ParkMap from 'components/map/ParkMap';
import OngoingSessionsSheet from 'components/ongoingParking/OngoingSessionsSheet';
import ParkingZoneSearchSheet from 'components/park/ParkingZoneSearchSheet';
import AccountContext from 'context/AccountContext';
import { UNLOCK_URL_LOCKED_SEGMENT } from 'graphql/mutations/unlockLockedUrlSegment';
import { GET_CUSTOMER } from 'graphql/queries/getCustomer';
import { useFetchParkZones } from 'hooks/useFetchParkZones';
import React, { useContext, useEffect } from 'react';
import { Dimensions } from 'react-native';
import { urlUnlockingSegment } from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import { useMapStore } from 'stores/mapStore';
import {
  Mutation,
  MutationUnlockUrlLockedSegmentArgs,
} from 'types/generatedSchemaTypes';
import { mapFilter, parkMapParkzoneFilter } from 'utils/parkUtils';
import { useParkzoneStore } from '../stores/parkzoneStore';

export type ParamList = {
  Park: {
    mode: string;
    parkingZoneUid?: string;
    ticket?: string;
  };
};
const { height } = Dimensions.get('window');

const ParkScreen: React.FC<{
  showSheet?: boolean;
  showBeforeMapLoaded?: boolean;
}> = ({ showSheet = true, showBeforeMapLoaded = false }) => {
  const { params } = useRoute<RouteProp<ParamList, 'Park'>>();
  const { mergedParkZonesData, loading, fetchAllParkZones } =
    useFetchParkZones();
  const {
    customer: { householdBenefits, permits },
  } = useContext(AccountContext);
  const filters = useMapStore((state) => state.mapFilters);

  const currentAppCountry = useAuthStore(
    (state) => state.currentCustomerCountry
  );
  const webUrlUnlockingState = useReactiveVar(urlUnlockingSegment);

  const { parkZonesData, setParkzoneData } = useParkzoneStore();
  useEffect(() => {
    if (filters.permits === false) {
      const filteredByParkzoneFilter = parkMapParkzoneFilter(
        mergedParkZonesData?.parkZonesV2?.content ?? [],
        householdBenefits,
        permits,
        true
      );
      const finalFilteredData = (filteredByParkzoneFilter ?? []).filter(
        (parkZone) => mapFilter(parkZone, filters)
      );
      setParkzoneData(finalFilteredData ?? []);
    }
  }, [
    currentAppCountry,
    setParkzoneData,
    mergedParkZonesData,
    householdBenefits,
    permits,
    filters,
  ]);

  const clearUrlUnlocking = () => {
    setTimeout(() => {
      urlUnlockingSegment(null);
      (window as any).open(webUrlUnlockingState?.logoutUrl);
    }, 1500);
  };
  const [unlockUrlSegment] = useMutation<
    Mutation,
    MutationUnlockUrlLockedSegmentArgs
  >(UNLOCK_URL_LOCKED_SEGMENT, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_CUSTOMER,
        errorPolicy: 'all',
      },
    ],
    onCompleted: () => {
      clearUrlUnlocking();
      showToast('success.tariffUnlock', 'success');
    },
    onError: async () => {
      clearUrlUnlocking();
      showToast('error.tariffUnlockFailed', 'error');
    },
  });

  const mapPadding = React.useMemo(
    () => ({
      top: 0,
      bottom:
        theme.normalising.heightPixel(400) +
        (theme.normalising.heightPixel(145) -
          theme.normalising.heightPixel(height) * 0.13) -
        theme.normalising.heightPixel(50),
      left: 0,
      right: 0,
    }),
    []
  );

  useEffect(() => {
    fetchAllParkZones();
  }, [fetchAllParkZones]);

  useEffect(() => {
    const token = params?.ticket;
    if (token && webUrlUnlockingState) {
      unlockUrlSegment({
        variables: {
          input: {
            token,
            pricingSchemeId: webUrlUnlockingState?.pricingScheme,
            zoneCode: webUrlUnlockingState?.zoneCode as string,
            customerSegmentUid:
              webUrlUnlockingState?.customerSegmentUid as string,
            service: webUrlUnlockingState?.service,
            provider: webUrlUnlockingState?.provider,
            hasUnlocked: webUrlUnlockingState?.hasUnlocked as boolean,
          },
        },
      });
    }
  }, [params?.ticket, webUrlUnlockingState, unlockUrlSegment]);

  return (
    <>
      <ParkMap
        showBeforeMapLoaded={showBeforeMapLoaded}
        parkingZonesData={parkZonesData}
        mapPadding={mapPadding}
        zonesLoading={loading}
        showZoomedTooFar
      />
      {showSheet && <ParkingZoneSearchSheet />}
      {showSheet && <OngoingSessionsSheet />}
    </>
  );
};

export default ParkScreen;
